import type { ComboboxItem } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useWorkPeriodsMeasureSingular } from '../../../../api/work-periods-client/work-periods-client.hooks';
import {
  FlowByPhaseDataOverTime,
  Measure,
  TimeAllocationType,
} from '../../../../api/work-periods-client/work-periods-client.type';
import { useFlowByPhaseTabStore } from '../../../../store/strategy-store/strategy-store';
import {
  setFlowByPhaseData,
  setFlowByPhaseSelectedDate,
} from '../../../../store/strategy-store/strategy-store.actions';
import { useInitiativePerformance } from '../initiative-performance.hooks';
import { PHASES_METADATA } from './flow-by-phase-tab.constants';
import { getLatestDateWithData } from './flow-by-phase-tab.helpers';
import type { ChartDataItem } from './main-section/flow-by-phase-chart/flow-by-phase-chart.type';

/**
 * Hook to fetch and manage flow by phase data for an initiative.
 *
 * This hook fetches flow by phase data over time for the specified initiative.
 * When data is received, it updates the global state with the flow by phase data
 * and sets the selected date to the most recent date that has actual data.
 *
 * @param {string} [epicId] - Optional epic ID to filter data by a specific epic
 * @returns {Object} Object containing loading state
 * @returns {boolean} Object.isLoading - Whether data is currently being fetched
 */
const useFlowByPhaseData = (epicId?: string): { isLoading: boolean } => {
  const initiative = useInitiativePerformance();

  const { data, query } = useWorkPeriodsMeasureSingular<{ flow_by_phase: FlowByPhaseDataOverTime }>({
    payload: {
      start_date: initiative?.effective_start_date
        ? dayjs(initiative?.effective_start_date).format('YYYY-MM-DD')
        : dayjs(initiative?.start_date).format('YYYY-MM-DD'),
      end_date: initiative?.effective_end_date
        ? dayjs(initiative?.effective_end_date).format('YYYY-MM-DD')
        : dayjs(initiative?.end_date).format('YYYY-MM-DD'),
      initiative_id: epicId ? undefined : initiative?.id,
      measure_name: [Measure.FlowByPhase],
      time_allocation_type: TimeAllocationType.Monthly,
      epic_id: epicId || undefined,
    },
  });

  useEffect(() => {
    if (data?.flow_by_phase) {
      setFlowByPhaseData(data.flow_by_phase);

      const latestDate = getLatestDateWithData(data.flow_by_phase);

      if (latestDate) {
        setFlowByPhaseSelectedDate(latestDate);
      }
    } else {
      setFlowByPhaseData(null);
    }
  }, [data?.flow_by_phase]);

  return { isLoading: query.isFetching };
};

/**
 * Hook that provides date options for the Flow By Phase date selector.
 *
 * This hook processes the flow by phase data to extract dates that have meaningful data
 * (non-null values greater than 0). It formats these dates for display in a dropdown
 * and sorts them in descending order (most recent first).
 *
 * @returns {ComboboxItem[] | []} An array of date options formatted as ComboboxItems,
 *                                or an empty array if no data is available
 */
const useDateOptions = (): ComboboxItem[] | [] => {
  const data = useFlowByPhaseTabStore((state) => state.data);

  if (!data) {
    return [];
  }

  try {
    return Object.entries(data)
      .filter(([_, values]) => {
        if (!values || typeof values !== 'object') {
          return false;
        }

        return Object.entries(values).some(([_, value]) => {
          return value !== null && typeof value === 'number' && value > 0;
        });
      })
      .map(([date]) => ({
        value: date,
        label: dayjs(date).format('MMM, YYYY'),
      }))
      .sort((a, b) => dayjs(b.value).diff(dayjs(a.value)));
  } catch (error) {
    console.error('Error processing date options:', error);
    return [];
  }
};

/**
 * Hook that provides chart data for the Flow By Phase visualization.
 *
 * This hook processes the flow by phase data to create a time series chart dataset.
 * It handles filtering out dates with no meaningful data, sorting by date,
 * and nulling out values after the selected date to show historical progression.
 *
 * @returns {Object} An object containing:
 *   - chartData: Array of processed data points for the chart
 *   - currentDate: The selected date or most recent date with data
 *   - phaseMetadata: Metadata about the phases being displayed
 */
const useChartData = () => {
  const selectedDate = useFlowByPhaseTabStore((state) => state.selectedDate);
  const data = useFlowByPhaseTabStore((state) => state.data);

  return useMemo(() => {
    if (!data) {
      return { chartData: [], currentDate: null };
    }

    const allChartData = Object.entries(data)
      .map(([date, values]): ChartDataItem => {
        const valueObj = typeof values === 'object' && values !== null ? (values as Record<string, number | null>) : {};

        return {
          date,
          ...valueObj,
        };
      })
      .filter((item) => {
        return Object.entries(item).some(([key, value]) => {
          if (key === 'date') return false;
          return value !== null && typeof value === 'number' && value > 0;
        });
      })
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    // Keep all dates but null out values after selected date
    const processedChartData = allChartData.map((item): ChartDataItem => {
      if (selectedDate && dayjs(item.date).diff(selectedDate) > 0) {
        // Keep the date but null out all phase values
        const nulledItem: ChartDataItem = { date: item.date };
        PHASES_METADATA.forEach((phase) => {
          nulledItem[`tasks_${phase.id}`] = null;
          nulledItem[`points_${phase.id}`] = null;
        });
        return nulledItem;
      }
      return item;
    });

    // Get the last date with data (most recent)
    const lastDate = selectedDate || allChartData[allChartData.length - 1]?.date;

    return {
      chartData: processedChartData,
      currentDate: lastDate,
      phaseMetadata: PHASES_METADATA,
    };
  }, [data, selectedDate]);
};

export { useChartData, useDateOptions, useFlowByPhaseData };
